// Use as a wrapper to catch the clicks outside the content

import { useRef, useEffect } from 'react';

const IGNORE_SELECTORS = [
  '.select-floating',
  '.multi-select-floating',
  '.select-inline',
  '.multi-select-inline',
];

// Registry for all active GetClickOutside instances
const instances = new Set();
// Single global listener flag
let isListening = false;

// Global handler for document events
const handleDocumentEvent = (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;

  // Ignore clicks inside floating or inline select blocks
  if (IGNORE_SELECTORS.some((selector) => target.closest(selector))) return;

  // Go from topmost instance to bottom
  const list = Array.from(instances);
  for (let i = list.length - 1; i >= 0; i--) {
    const inst = list[i];
    const node = inst.wrapRef.current;
    if (!node) continue;
    if (!inst.activeRef.current) continue;

    // If click is outside this wrapper, handle it and stop
    if (!node.contains(target)) {
      if (inst.onClickRef.current) {
        inst.onClickRef.current();
      }
      break; // Do not propagate to lower layers
    }
  }
};

const ensureListeners = () => {
  if (isListening) return;
  if (typeof document === 'undefined') return; // SSR guard

  document.addEventListener('mousedown', handleDocumentEvent);
  document.addEventListener('touchstart', handleDocumentEvent);
  isListening = true;
};

const cleanupListeners = () => {
  if (!isListening) return;
  if (instances.size > 0) return; // Still have active instances
  if (typeof document === 'undefined') return;

  document.removeEventListener('mousedown', handleDocumentEvent);
  document.removeEventListener('touchstart', handleDocumentEvent);
  isListening = false;
};

export default function GetClickOutside({ children, active, onClick }) {
  const wrapRef = useRef(null);
  const activeRef = useRef(active);
  const onClickRef = useRef(onClick);

  // Keep refs up to date with props
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  // Register instance in global registry
  useEffect(() => {
    const instance = { wrapRef, activeRef, onClickRef };
    instances.add(instance);
    ensureListeners();

    return () => {
      instances.delete(instance);
      cleanupListeners();
    };
  }, []);

  return (
    <div className='get-click-outside' ref={wrapRef}>
      {children}
    </div>
  );
}
